import { useState } from 'react';
import { apiClient } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Ruler, User, Activity, Target, Scale, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { id: 'basics', title: 'Ton profil', subtitle: 'On commence par les bases' },
  { id: 'activity', title: 'Ton activite', subtitle: 'Pour calibrer tes besoins' },
  { id: 'goal', title: 'Ton objectif', subtitle: 'On adapte ta nutrition' },
];

export default function OnboardingWizard({ userName, onComplete }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ height: '', gender: '', weight: '', activity_level: '', nutrition_goal: '' });
  const [saving, setSaving] = useState(false);

  const handleFinish = async () => {
    if (!form.height || !form.gender || !form.activity_level || !form.nutrition_goal) {
      toast.error('Remplis tous les champs');
      return;
    }
    setSaving(true);
    try {
      await apiClient.post('/user/onboarding', {
        height: parseFloat(form.height),
        gender: form.gender,
        activity_level: form.activity_level,
        nutrition_goal: form.nutrition_goal,
        weight: form.weight ? parseFloat(form.weight) : null,
      });
      toast.success('Profil configure !');
      onComplete();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const canNext = () => {
    if (step === 0) return form.height && form.gender;
    if (step === 1) return form.activity_level;
    if (step === 2) return form.nutrition_goal;
    return false;
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center p-6">
      <div className="w-full max-w-sm" data-testid="onboarding-wizard">
        {/* Progress */}
        <div className="flex gap-1 mb-8">
          {STEPS.map((_, i) => (
            <div key={i} className={`flex-1 h-1 rounded-full transition-all ${i <= step ? 'bg-[#FF6B35]' : 'bg-[#2A2A2E]'}`} />
          ))}
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-xs text-[#FF6B35] font-bold uppercase tracking-widest mb-1">Bienvenue {userName}</p>
          <h1 className="text-3xl text-white font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{STEPS[step].title}</h1>
          <p className="text-sm text-zinc-400 mt-1">{STEPS[step].subtitle}</p>
        </div>

        {/* Step 0: Basics */}
        {step === 0 && (
          <div className="space-y-4 animate-fade-in">
            <OptionSelect label="Sexe" icon={<User className="w-4 h-4" />} value={form.gender} onChange={v => setForm({...form, gender: v})}
              options={[{ value: 'male', label: 'Homme' }, { value: 'female', label: 'Femme' }]} testId="onboard-gender" />
            <div>
              <label className="text-xs text-zinc-400 flex items-center gap-1 mb-1.5"><Ruler className="w-3 h-3" /> Taille (cm)</label>
              <Input type="number" min="100" max="250" value={form.height} onChange={e => setForm({...form, height: e.target.value})}
                placeholder="172" className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono text-lg h-12" data-testid="onboard-height" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 flex items-center gap-1 mb-1.5"><Scale className="w-3 h-3" /> Poids actuel (kg) <span className="text-zinc-600">optionnel</span></label>
              <Input type="number" min="30" max="250" step="0.1" value={form.weight} onChange={e => setForm({...form, weight: e.target.value})}
                placeholder="75" className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono text-lg h-12" data-testid="onboard-weight" />
            </div>
          </div>
        )}

        {/* Step 1: Activity */}
        {step === 1 && (
          <div className="space-y-3 animate-fade-in">
            {[
              { value: 'sedentaire', label: 'Sedentaire', desc: 'Travail de bureau, peu de sport' },
              { value: 'leger', label: 'Leger', desc: '1-2 seances / semaine' },
              { value: 'modere', label: 'Modere', desc: '3-5 seances / semaine' },
              { value: 'actif', label: 'Actif', desc: '6-7 seances / semaine' },
              { value: 'tres_actif', label: 'Tres actif', desc: '2 seances / jour ou travail physique' },
            ].map(o => (
              <button key={o.value} onClick={() => setForm({...form, activity_level: o.value})}
                className={`w-full p-4 rounded-lg text-left transition-all ${form.activity_level === o.value ? 'bg-[#FF6B35]/15 border-2 border-[#FF6B35]' : 'bg-[#1B1B1B] border-2 border-[#2A2A2E]'}`}
                data-testid={`onboard-activity-${o.value}`}>
                <p className={`text-sm font-bold ${form.activity_level === o.value ? 'text-[#FF6B35]' : 'text-white'}`}>{o.label}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{o.desc}</p>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Goal */}
        {step === 2 && (
          <div className="space-y-3 animate-fade-in">
            {[
              { value: 'perte', label: 'Perte de poids', desc: 'Deficit de 300 kcal / jour', icon: '🔥' },
              { value: 'perte_gras', label: 'Perte de gras', desc: 'Deficit de 400 kcal, proteines elevees', icon: '🎯' },
              { value: 'seche', label: 'Seche', desc: 'Deficit de 500 kcal / jour', icon: '⚡' },
              { value: 'maintien', label: 'Maintien', desc: 'Garder mon poids actuel', icon: '⚖️' },
              { value: 'prise', label: 'Prise de masse', desc: 'Surplus de 300 kcal / jour', icon: '💪' },
            ].map(o => (
              <button key={o.value} onClick={() => setForm({...form, nutrition_goal: o.value})}
                className={`w-full p-4 rounded-lg text-left transition-all flex items-center gap-3 ${form.nutrition_goal === o.value ? 'bg-[#FF6B35]/15 border-2 border-[#FF6B35]' : 'bg-[#1B1B1B] border-2 border-[#2A2A2E]'}`}
                data-testid={`onboard-goal-${o.value}`}>
                <span className="text-2xl">{o.icon}</span>
                <div>
                  <p className={`text-sm font-bold ${form.nutrition_goal === o.value ? 'text-[#FF6B35]' : 'text-white'}`}>{o.label}</p>
                  <p className="text-xs text-zinc-400">{o.desc}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-3 mt-8">
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1 border-zinc-700 text-zinc-400">
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
          )}
          {step < 2 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canNext()} className="flex-1 bg-[#FF6B35] text-white hover:bg-[#FF8C55] disabled:opacity-40" data-testid="onboard-next">
              Suivant <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleFinish} disabled={saving || !canNext()} className="flex-1 bg-[#FF6B35] text-white hover:bg-[#FF8C55] disabled:opacity-40" data-testid="onboard-finish">
              {saving ? 'Configuration...' : 'C\'est parti !'}
            </Button>
          )}
        </div>

        {/* Skip */}
        <button onClick={() => { sessionStorage.setItem('onboarding_skipped', '1'); onComplete(); }} className="w-full text-center text-xs text-zinc-600 mt-4 hover:text-zinc-400" data-testid="onboard-skip">
          Configurer plus tard
        </button>
      </div>
    </div>
  );
}

function OptionSelect({ label, icon, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 flex items-center gap-1 mb-1.5">{icon} {label}</label>
      <div className="flex gap-2">
        {options.map(o => (
          <button key={o.value} onClick={() => onChange(o.value)}
            className={`flex-1 py-3 rounded-lg text-sm font-bold transition-all ${value === o.value ? 'bg-[#FF6B35] text-white' : 'bg-[#1B1B1B] text-zinc-400 border border-[#2A2A2E]'}`}
            data-testid={`${testId}-${o.value}`}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
